// The overflow surface: a bottom sheet on phones, a dropdown on desktop. One
// implementation, two looks — the difference is entirely CSS, so the focus trap,
// the Escape handling and the scroll behaviour cannot drift apart.
//
// Also used by the variant chips when a room has more styles than fit inline,
// which is why openWith() takes arbitrary rows rather than only MenuItems.

import { MENU_GROUP_LABEL_KEY, MENU_GROUP_ORDER, type MenuContext, type MenuGroup, type MenuItem } from './types.ts';

export interface SheetRow {
  id: string;
  label: string;
  icon?: string;
  ariaLabel?: string;
  group?: MenuGroup;
  pressed?: boolean;
  disabled?: boolean;
  /** For the language sheet and variant sheet — a radio-style current choice. */
  selected?: boolean;
  onSelect(): void;
}

export class MenuSheet {
  private readonly root: HTMLDivElement;
  private readonly list: HTMLDivElement;
  private readonly backdrop: HTMLDivElement;
  private readonly titleEl: HTMLHeadingElement;
  private open = false;
  private lastFocused: HTMLElement | null = null;
  private readonly onKeydown = (e: KeyboardEvent) => this.handleKeydown(e);

  constructor(private readonly ctx: MenuContext) {
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'menu-sheet__backdrop';
    // Must start hidden. It is position:fixed inset:0, so an un-hidden backdrop
    // sits over the entire panorama and eats every drag-look and hotspot tap —
    // invisible at opacity 0, and indistinguishable from "the tour is frozen".
    this.backdrop.hidden = true;
    this.backdrop.addEventListener('click', () => this.close());

    this.root = document.createElement('div');
    this.root.className = 'menu-sheet';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.hidden = true;

    const header = document.createElement('header');
    header.className = 'menu-sheet__header';
    this.titleEl = document.createElement('h2');
    this.titleEl.className = 'menu-sheet__title';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'modal__close';
    close.textContent = '×';
    close.setAttribute('aria-label', ctx.t('common.close'));
    close.addEventListener('click', () => this.close());
    header.append(this.titleEl, close);

    this.list = document.createElement('div');
    this.list.className = 'menu-sheet__list';

    this.root.append(header, this.list);
    document.body.append(this.backdrop, this.root);
  }

  isOpen(): boolean {
    return this.open;
  }

  /** Renders MenuItems, grouped under their section headings. */
  openWithItems(items: MenuItem[], title: string, activate: (item: MenuItem) => void): void {
    const rows: SheetRow[] = items.map((item) => ({
      id: item.id,
      label: this.ctx.t(item.labelKey),
      icon: item.icon,
      ariaLabel: this.ariaFor(item),
      group: item.group,
      pressed: item.isPressed?.(this.ctx),
      disabled: item.isEnabled ? !item.isEnabled(this.ctx) : false,
      onSelect: () => activate(item),
    }));
    this.openWith(rows, title);
  }

  openWith(rows: SheetRow[], title: string): void {
    this.titleEl.textContent = title;
    this.list.replaceChildren();

    const grouped = rows.some((r) => r.group);
    if (grouped) {
      for (const group of MENU_GROUP_ORDER) {
        const inGroup = rows.filter((r) => r.group === group);
        if (inGroup.length === 0) continue;
        const heading = document.createElement('h3');
        heading.className = 'menu-sheet__group';
        heading.textContent = this.ctx.t(MENU_GROUP_LABEL_KEY[group]);
        this.list.append(heading);
        for (const row of inGroup) this.list.append(this.renderRow(row));
      }
    } else {
      for (const row of rows) this.list.append(this.renderRow(row));
    }

    this.show();
  }

  private renderRow(row: SheetRow): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'menu-sheet__row';
    btn.dataset.menuId = row.id;
    if (row.disabled) btn.disabled = true;
    if (row.pressed !== undefined) btn.setAttribute('aria-pressed', String(row.pressed));
    if (row.selected !== undefined) btn.setAttribute('aria-checked', String(row.selected));
    btn.setAttribute('aria-label', row.ariaLabel ?? row.label);

    if (row.icon) {
      const icon = document.createElement('span');
      icon.className = 'menu-sheet__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = row.icon;
      btn.append(icon);
    }
    const label = document.createElement('span');
    label.className = 'menu-sheet__label';
    label.textContent = row.label;
    // The full string stays reachable when the label ellipsises — English runs
    // up to ~30% longer than pt-BR on some of these.
    label.title = row.label;
    btn.append(label);

    btn.addEventListener('click', () => {
      // Close first: several actions open a modal or a panel, and leaving the
      // sheet up would stack two dialogs.
      this.close();
      row.onSelect();
    });
    return btn;
  }

  private ariaFor(item: MenuItem): string {
    const pressed = item.isPressed?.(this.ctx) ?? false;
    if (pressed && item.ariaPressedKey) return this.ctx.t(item.ariaPressedKey);
    return item.ariaKey ? this.ctx.t(item.ariaKey) : this.ctx.t(item.labelKey);
  }

  private show(): void {
    if (this.open) return;
    this.lastFocused = document.activeElement as HTMLElement | null;
    this.open = true;
    this.root.hidden = false;
    this.backdrop.hidden = false;
    // Two frames: one to un-hide, one so the transition has a start value.
    requestAnimationFrame(() => {
      this.root.classList.add('is-open');
      this.backdrop.classList.add('is-open');
    });
    document.body.classList.add('has-sheet');
    document.addEventListener('keydown', this.onKeydown);
    this.list.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
  }

  close(): void {
    if (!this.open) return;
    this.open = false;
    this.root.classList.remove('is-open');
    this.backdrop.classList.remove('is-open');
    document.body.classList.remove('has-sheet');
    document.removeEventListener('keydown', this.onKeydown);
    const done = (): void => {
      if (this.open) return; // reopened mid-transition
      this.root.hidden = true;
      this.backdrop.hidden = true;
    };
    window.setTimeout(done, 220);
    this.lastFocused?.focus?.();
    this.lastFocused = null;
  }

  private handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.stopPropagation();
      this.close();
      return;
    }
    if (e.key !== 'Tab') return;
    // Focus trap. Without it, Tab walks out of the sheet onto the canvas and
    // the buttons underneath, which on a phone are visually covered.
    const focusable = [...this.root.querySelectorAll<HTMLElement>('button:not(:disabled)')];
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  destroy(): void {
    document.removeEventListener('keydown', this.onKeydown);
    this.root.remove();
    this.backdrop.remove();
  }
}
